import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError, beneficiaries as benApi, documents as docApi, newIdempotencyKey, orders as ordersApi, quotes as quotesApi } from "../api/client";
import type { Beneficiary, DocumentRef, Nature, Quote } from "../api/types";
import { useMe } from "../auth/session";
import { BankComparison, QuoteRows } from "../components/QuoteBreakdown";
import { Page } from "../components/Shell";
import { ErrorBox, Field, Icon, Spinner, YSymbol, errorMessage } from "../components/ui";
import { useAsync } from "../hooks/useAsync";
import { useCountdown } from "../hooks/useCountdown";
import { fmt, t } from "../i18n";
import { fileSize, timeHMS } from "../lib/format";
import { brl, maskDecimalInput, maskedToCents, mmss } from "../lib/money";
import { railLabel, railNetworkName } from "../lib/rail";

type QuoteState =
  | { kind: "idle"; message?: string }
  | { kind: "loading" }
  | { kind: "ready"; quote: Quote; at: Date }
  | { kind: "error"; error: unknown };

export function NovoPagamento() {
  const me = useMe();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { data: suppliers, error: supErr, loading: supLoading, reload } = useAsync(() => benApi.list(), []);
  const active = useMemo(() => (suppliers ?? []).filter((s) => s.status === "active"), [suppliers]);

  const [supplierId, setSupplierId] = useState(sp.get("fornecedor") ?? "");
  const [amount, setAmount] = useState(() => (sp.get("usd") ? maskDecimalInput(sp.get("usd")!) : ""));
  const [nature, setNature] = useState<Nature>((sp.get("natureza") as Nature) || "goods_import");
  const [reference, setReference] = useState(sp.get("ref") ?? "");
  const [invoice, setInvoice] = useState<DocumentRef | null>(null);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceErr, setInvoiceErr] = useState<string | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [qs, setQs] = useState<QuoteState>({ kind: "idle" });
  const quoteGen = useRef(0); // invalida respostas de cotação que chegam depois de o formulário mudar
  const orderKey = useRef<string | null>(null); // Idempotency-Key estável por cotação: retry após timeout não cria segundo pedido
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!supplierId && active.length === 1) setSupplierId(active[0]!.id);
  }, [active, supplierId]);

  const supplier: Beneficiary | undefined = active.find((s) => s.id === supplierId);

  const invalidate = useCallback((message: string) => {
    quoteGen.current++;
    setQs((cur) => (cur.kind === "ready" || cur.kind === "loading" ? { kind: "idle", message } : cur));
  }, []);

  const expiresAt = qs.kind === "ready" ? qs.quote.expiresAt : null;
  const countdown = useCountdown(expiresAt, () => invalidate(t.newPayment.expired));

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      invalidate(t.newPayment.invalidated);
    };
  }

  async function onFile(f: File | undefined) {
    if (!f) return;
    setInvoiceErr(null);
    if (f.size > 10 * 1024 * 1024) return setInvoiceErr(t.onboarding.fileTooBig);
    if (!/^(application\/pdf|image\/(png|jpe?g))$/.test(f.type)) return setInvoiceErr(t.onboarding.fileType);
    setInvoiceBusy(true);
    try {
      const r = await docApi.upload(f, "invoice");
      setInvoice(r.document);
      invalidate(t.newPayment.invalidated);
    } catch (e) {
      setInvoiceErr(errorMessage(e));
    } finally {
      setInvoiceBusy(false);
    }
  }

  async function requestQuote() {
    setFormErr(null);
    setCreateErr(null);
    if (!supplier) return setFormErr(t.newPayment.supplierPlaceholder);
    const cents = maskedToCents(amount);
    if (cents === "0") return setFormErr(t.newPayment.amountInvalid);
    if (!invoice) {
      setInvoiceErr(t.newPayment.invoiceRequired);
      return;
    }
    const my = ++quoteGen.current;
    setQs({ kind: "loading" });
    try {
      const r = await quotesApi.create({ beneficiaryId: supplier.id, usdCents: cents, nature });
      if (my !== quoteGen.current) return; // o formulário mudou enquanto a cotação viajava: descarta
      orderKey.current = newIdempotencyKey();
      setQs({ kind: "ready", quote: r.quote, at: new Date() });
    } catch (e) {
      if (my === quoteGen.current) setQs({ kind: "error", error: e });
    }
  }

  async function createOrder() {
    if (qs.kind !== "ready" || !invoice) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const r = await ordersApi.create({ quoteId: qs.quote.id, invoiceDocumentId: invoice.id, reference: reference.trim() || undefined }, orderKey.current ?? newIdempotencyKey());
      navigate(`/pagamentos/${r.order.id}`, { replace: false });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 410 || e.code === "quote_expired")) {
        setQs({ kind: "idle", message: t.newPayment.quoteExpiredOnCreate });
      } else {
        setCreateErr(errorMessage(e));
      }
    } finally {
      setCreating(false);
    }
  }

  const networkName = railNetworkName(supplier?.rail);

  return (
    <Page
      title={t.newPayment.title}
      crumb={t.newPayment.crumb}
      backTo="/"
      actions={
        <Link to="/" className="btn ghost">
          {t.common.cancel}
        </Link>
      }
    >
      <div className="two">
        <form
          className="card"
          style={{ padding: 24, display: "flex", flexDirection: "column", gap: 18 }}
          onSubmit={(e) => {
            e.preventDefault();
            void requestQuote();
          }}
          noValidate
        >
          <h2>{t.newPayment.formTitle}</h2>
          {supErr ? <ErrorBox error={supErr} onRetry={reload} /> : null}
          <Field label={t.newPayment.supplier} error={!supLoading && active.length === 0 ? t.newPayment.supplierNone : null}>
            {(id, d) => (
              <div className="inp">
                <select id={id} value={supplierId} onChange={(e) => onChange(setSupplierId)(e.target.value)} aria-describedby={d} disabled={supLoading || active.length === 0} data-testid="supplier">
                  <option value="">{supLoading ? t.common.loading : t.newPayment.supplierPlaceholder}</option>
                  {active.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.legalName} · {railLabel(s.rail)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </Field>
          <div className="grid2">
            <Field label={t.newPayment.amount}>
              {(id, d) => (
                <div className="inp">
                  <span className="pre mono">USD</span>
                  <input id={id} className="mono" inputMode="decimal" value={amount} placeholder="0,00" onChange={(e) => onChange(setAmount)(maskDecimalInput(e.target.value))} aria-describedby={d} data-testid="amount" />
                </div>
              )}
            </Field>
            <Field label={t.newPayment.nature}>
              {(id) => (
                <div className="inp">
                  <select id={id} value={nature} onChange={(e) => onChange(setNature)(e.target.value as Nature)} data-testid="nature">
                    <option value="goods_import">{t.newPayment.natures.goods_import}</option>
                    <option value="service_import">{t.newPayment.natures.service_import}</option>
                  </select>
                </div>
              )}
            </Field>
          </div>
          <Field label={t.newPayment.invoice} error={invoiceErr}>
            {(id, d) => (
              <div className={`attach ${invoice ? "ok" : ""} ${invoiceErr ? "err" : ""}`}>
                {Icon.clip()}
                <div className="txt">
                  {invoice ? (
                    <>
                      <b>{invoice.fileName}</b>
                      <span>{fmt(t.newPayment.invoiceAttached, { name: "", size: fileSize(invoice.sizeBytes) }).replace(/^ · /, "")}</span>
                    </>
                  ) : (
                    <span>{invoiceBusy ? t.newPayment.invoiceUploading : t.newPayment.invoiceNone}</span>
                  )}
                </div>
                <input id={id} ref={fileRef} type="file" className="sr-only" accept="application/pdf,image/png,image/jpeg" aria-describedby={d} onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="invoice" />
                <label htmlFor={id} className="btn ghost sm act" style={{ cursor: "pointer" }} aria-disabled={invoiceBusy}>
                  {invoiceBusy ? t.newPayment.invoiceUploading : invoice ? t.newPayment.invoiceReplace : t.newPayment.invoiceAttach}
                </label>
              </div>
            )}
          </Field>
          <Field label={t.newPayment.reference}>
            {(id) => (
              <div className="inp">
                <input id={id} value={reference} placeholder={t.newPayment.referencePh} onChange={(e) => setReference(e.target.value)} />
              </div>
            )}
          </Field>
          <div className="form-info">
            {Icon.lock()}
            <span>{t.newPayment.travelRule}</span>
          </div>
          {formErr && <div className="form-error" role="alert">{formErr}</div>}
          <div>
            <button type="submit" className="btn" disabled={qs.kind === "loading" || invoiceBusy || active.length === 0}>
              {qs.kind === "ready" ? t.newPayment.newQuote : t.newPayment.askQuote}
            </button>
          </div>
        </form>

        <div className="card" aria-live="polite">
          <div className="sniff">
            <span className="l">
              {qs.kind === "loading" ? <span className="spin" aria-hidden="true" /> : <YSymbol size={24} />}
              {qs.kind === "ready" ? (
                <b>{t.newPayment.quoteReady}</b>
              ) : qs.kind === "loading" ? (
                <span>{t.newPayment.sniffing}</span>
              ) : qs.kind === "idle" ? (
                <span>{qs.message ?? t.newPayment.quoteIdle}</span>
              ) : (
                <span>{t.common.errorTitle}</span>
              )}
            </span>
            {qs.kind === "ready" && <span>{fmt(t.newPayment.quoteUpdatedAt, { time: timeHMS(qs.at) })}</span>}
          </div>
          <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column" }}>
            {qs.kind === "idle" && <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{qs.message ? t.newPayment.nothingCharged : t.newPayment.quoteIdleBody}</div>}
            {qs.kind === "loading" && <div className="muted" style={{ fontSize: 13 }}>{t.newPayment.sniffingBody}</div>}
            {qs.kind === "error" && <QuoteError error={qs.error} me={me} onRetry={requestQuote} />}
            {qs.kind === "ready" && (
              <>
                <QuoteRows quote={qs.quote} network={networkName} />
                <div className="qrow total">
                  <span className="k">{t.newPayment.youPay}</span>
                  <span className="v mono" data-testid="total">{brl(qs.quote.totalCents)}</span>
                </div>
                <div className="qrow" style={{ borderBottom: 0, paddingTop: 2 }} title={t.newPayment.vetTooltip}>
                  <span className="k">{t.newPayment.vet}</span>
                  <span className="v mono" data-testid="vet">{vetLabel(qs.quote.totalCents, qs.quote.usdCents)}</span>
                </div>
                <div className={`lock ${countdown.low ? "low" : ""}`} role="timer" aria-live="off">
                  {Icon.lock()}
                  {t.newPayment.locked}
                  <span className="t mono" data-testid="timer">{mmss(countdown.secondsLeft)}</span>
                </div>
                <BankComparison quote={qs.quote} disclaimer={me.legalTexts?.bankDisclaimer} />
                {createErr && <div className="form-error" role="alert" style={{ marginTop: 12 }}>{createErr}</div>}
                <button type="button" className="btn full" style={{ marginTop: 16 }} onClick={createOrder} disabled={creating || countdown.expired} data-testid="generate-pix">
                  {creating ? t.newPayment.generating : fmt(t.newPayment.generatePix, { total: brl(qs.quote.totalCents) })}
                </button>
                <p className="legal" style={{ marginTop: 12 }}>{me.legalTexts?.partnerDisclosure ?? t.brand.legalQuote}</p>
              </>
            )}
          </div>
        </div>
      </div>
      {supLoading && !suppliers && <Spinner />}
    </Page>
  );
}

function QuoteError({ error, me, onRetry }: { error: unknown; me: ReturnType<typeof useMe>; onRetry: () => void }) {
  // limite/mínimo do parceiro: a API já manda a mensagem com o valor (ex.: "O parceiro limita a US$ 100.000 por dia…")
  if (error instanceof ApiError && (error.code === "partner_limit_exceeded" || error.code === "partner_minimum_amount" || error.code === "partner_account_unavailable")) return <div className="form-error" role="alert">{error.message || t.newPayment.partnerRejected}</div>;
  if (error instanceof ApiError && (error.status === 422 || error.code === "limit_exceeded")) {
    const details = (error.details ?? {}) as { violations?: string[]; limit?: string };
    const which = (details.violations ?? []).map((v) => (v === "monthly" ? t.newPayment.limitMonthly : t.newPayment.limitPerTx)).join(" e ") || t.newPayment.limitPerTx;
    const lim = me.company?.limits;
    return (
      <div className="stack s8">
        <div className="form-error" role="alert">
          {fmt(t.newPayment.limitExceeded, { which })}
          {lim && (
            <span style={{ display: "block", marginTop: 6 }} className="mono">
              {t.settings.perTx}: {brl(lim.perTxCents)} · {t.settings.monthly}: {brl(lim.monthlyCents)}
            </span>
          )}
        </div>
      </div>
    );
  }
  if (error instanceof ApiError && error.code === "beneficiary_not_active") return <div className="form-error" role="alert">{t.newPayment.beneficiaryNotActive}</div>;
  return <ErrorBox error={error} onRetry={onRetry} />;
}

/** VET equivalente: total em reais ÷ dólares da invoice, com 4 casas (como o VET do contrato de câmbio, para comparar direto). */
function vetLabel(totalCents: string, usdCents: string): string {
  const t = Number(totalCents), u = Number(usdCents);
  if (!u) return "—";
  return `R$ ${(t / u).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} por US$`;
}
